import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { money } from '../lib/format';
import { serviceIcon, serviceIcons } from '../lib/serviceIcons';
import {
  localizedServiceDescription,
  localizedServiceDuration,
  localizedServiceName,
} from '../lib/serviceI18n';
import { supabase } from '../lib/supabase';
import ConfirmBar from '../components/ConfirmBar';
import Sheet from '../components/Sheet';

function IconMark({ value, className = '' }) {
  const icon = serviceIcon(value);
  return (
    <span className={`material-symbol ${className}`} aria-hidden="true">
      {icon.symbol}
    </span>
  );
}

const blank = {
  name: '',
  name_ar: '',
  description: '',
  description_ar: '',
  price: '',
  estimated_minutes: '',
  duration_label: '',
  duration_label_ar: '',
  icon: 'handyman',
  sort_order: 0,
  is_active: true,
  is_featured: false,
};

function filled(value) {
  return Boolean(String(value || '').trim());
}

export default function ServicesPage() {
  const { isManager } = useAuth();
  const { t, locale } = useLocale();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  async function load() {
    const { data, error: queryError } = await supabase
      .from('services')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (queryError) {
      setError(queryError.message);
      return;
    }
    setRows(data || []);
  }

  useEffect(() => {
    load();
  }, []);

  function startCreate() {
    setEditingId(null);
    setDraft({ ...blank, sort_order: rows.length + 1 });
    setOpen(true);
  }

  function startEdit(row) {
    setEditingId(row.id);
    setDraft({
      name: row.name || '',
      name_ar: row.name_ar || '',
      description: row.description || '',
      description_ar: row.description_ar || '',
      price: row.price ?? '',
      estimated_minutes: row.estimated_minutes ?? '',
      duration_label: row.duration_label || '',
      duration_label_ar: row.duration_label_ar || '',
      icon: row.icon || 'handyman',
      sort_order: row.sort_order ?? 0,
      is_active: row.is_active,
      is_featured: row.is_featured,
    });
    setOpen(true);
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    const name = draft.name.trim();
    const nameAr = draft.name_ar.trim();
    const description = draft.description.trim();
    const descriptionAr = draft.description_ar.trim();
    const duration = draft.duration_label.trim();
    const durationAr = draft.duration_label_ar.trim();

    if (!name || !nameAr) {
      setSaving(false);
      setError(t('services.needBothNames'));
      return;
    }
    if (filled(description) !== filled(descriptionAr)) {
      setSaving(false);
      setError(t('services.needBothDescriptions'));
      return;
    }
    if (filled(duration) !== filled(durationAr)) {
      setSaving(false);
      setError(t('services.needBothDurations'));
      return;
    }

    const payload = {
      name,
      name_ar: nameAr,
      description: description || null,
      description_ar: descriptionAr || null,
      price: draft.price === '' ? null : Number(draft.price),
      estimated_minutes:
        draft.estimated_minutes === '' ? null : Number(draft.estimated_minutes),
      duration_label: duration || null,
      duration_label_ar: durationAr || null,
      icon: draft.icon,
      sort_order: Number(draft.sort_order) || 0,
      is_active: Boolean(draft.is_active),
      is_featured: Boolean(draft.is_featured),
    };
    const query = editingId
      ? supabase.from('services').update(payload).eq('id', editingId)
      : supabase.from('services').insert(payload);
    const { error: saveError } = await query;
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setOpen(false);
    load();
  }

  async function remove(id) {
    const { error: deleteError } = await supabase.from('services').delete().eq('id', id);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    setPendingDelete(null);
    load();
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <span className="kicker">{t('services.kicker')}</span>
          <h1>{t('services.title')}</h1>
          <p className="lede">{t('services.lede')}</p>
        </div>
        {isManager && (
          <button type="button" className="primary" onClick={startCreate}>
            {t('services.new')}
          </button>
        )}
      </header>
      {error && <p className="form-error">{error}</p>}
      {!isManager && <p className="empty">{t('services.viewOnly')}</p>}
      <div className="service-grid">
        {rows.map((row) => {
          const title = localizedServiceName(row, locale);
          const other =
            locale === 'ar' ? row.name : row.name_ar;
          const description =
            localizedServiceDescription(row, locale) || t('services.noDescription');
          const duration =
            localizedServiceDuration(row, locale) ||
            (row.estimated_minutes ? `${row.estimated_minutes} ${t('services.minutes')}` : '—');
          return (
            <article key={row.id} className={`service-card ${row.is_active ? '' : 'dim'}`}>
              <header>
                <span className="service-mark">
                  <IconMark value={row.icon} />
                  <span className="kicker">{t(`icon.${serviceIcon(row.icon).value}`)}</span>
                </span>
                {row.is_featured && <em className="pill">{t('services.featured')}</em>}
              </header>
              <h3>{title || t('common.service')}</h3>
              {other ? <p className="service-alt-name">{other}</p> : null}
              <p>{description}</p>
              <div className="meta">
                <span>{money(row.price)}</span>
                <span>{duration}</span>
              </div>
              {isManager &&
                (pendingDelete === row.id ? (
                  <ConfirmBar
                    message={t('services.removeConfirm')}
                    confirmLabel={t('services.remove')}
                    onCancel={() => setPendingDelete(null)}
                    onConfirm={() => remove(row.id)}
                  />
                ) : (
                  <div className="row-actions">
                    <button type="button" className="ghost" onClick={() => startEdit(row)}>
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      className="ghost danger"
                      onClick={() => setPendingDelete(row.id)}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                ))}
            </article>
          );
        })}
      </div>

      {open && (
        <Sheet
          variant="modal"
          title={editingId ? t('services.edit') : t('services.new')}
          subtitle={t('services.sheetSubtitle')}
          onClose={() => setOpen(false)}
        >
          <form className="sheet-form" onSubmit={save}>
            <div className="i18n-pair">
              <label>
                <span className="i18n-lang">{t('services.langEn')}</span>
                {t('services.nameEn')}
                <input
                  dir="ltr"
                  lang="en"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  required
                />
              </label>
              <label className="lang-ar">
                <span className="i18n-lang">{t('services.langAr')}</span>
                {t('services.nameAr')}
                <input
                  dir="rtl"
                  lang="ar"
                  value={draft.name_ar}
                  onChange={(e) => setDraft({ ...draft, name_ar: e.target.value })}
                  required
                />
              </label>
            </div>
            <div className="i18n-pair">
              <label>
                <span className="i18n-lang">{t('services.langEn')}</span>
                {t('services.descriptionEn')}
                <textarea
                  dir="ltr"
                  lang="en"
                  rows={3}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
              </label>
              <label className="lang-ar">
                <span className="i18n-lang">{t('services.langAr')}</span>
                {t('services.descriptionAr')}
                <textarea
                  dir="rtl"
                  lang="ar"
                  rows={3}
                  value={draft.description_ar}
                  onChange={(e) => setDraft({ ...draft, description_ar: e.target.value })}
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                {t('services.price')}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.price}
                  onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                />
              </label>
              <label>
                {t('services.minutes')}
                <input
                  type="number"
                  min="0"
                  value={draft.estimated_minutes}
                  onChange={(e) =>
                    setDraft({ ...draft, estimated_minutes: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="i18n-pair">
              <label>
                <span className="i18n-lang">{t('services.langEn')}</span>
                {t('services.durationEn')}
                <input
                  dir="ltr"
                  lang="en"
                  placeholder={t('services.durationHintEn')}
                  value={draft.duration_label}
                  onChange={(e) => setDraft({ ...draft, duration_label: e.target.value })}
                />
              </label>
              <label className="lang-ar">
                <span className="i18n-lang">{t('services.langAr')}</span>
                {t('services.durationAr')}
                <input
                  dir="rtl"
                  lang="ar"
                  placeholder={t('services.durationHintAr')}
                  value={draft.duration_label_ar}
                  onChange={(e) =>
                    setDraft({ ...draft, duration_label_ar: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="form-row">
              <label className="icon-field">
                {t('services.icon')}
                <div className="icon-preview">
                  <IconMark value={draft.icon} className="icon-preview-mark" />
                  <span>{t(`icon.${serviceIcon(draft.icon).value}`)}</span>
                </div>
                <div className="icon-grid" role="listbox" aria-label={t('services.icon')}>
                  {serviceIcons.map((icon) => (
                    <button
                      key={icon.value}
                      type="button"
                      role="option"
                      aria-selected={draft.icon === icon.value}
                      className={`icon-pick ${draft.icon === icon.value ? 'selected' : ''}`}
                      onClick={() => setDraft({ ...draft, icon: icon.value })}
                      title={t(`icon.${icon.value}`)}
                    >
                      <IconMark value={icon.value} />
                      <span>{t(`icon.${icon.value}`)}</span>
                    </button>
                  ))}
                </div>
              </label>
              <label>
                {t('services.sort')}
                <input
                  type="number"
                  value={draft.sort_order}
                  onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
                />
              </label>
            </div>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              />
              {t('services.visible')}
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={draft.is_featured}
                onChange={(e) => setDraft({ ...draft, is_featured: e.target.checked })}
              />
              {t('services.featuredCheck')}
            </label>
            <div className="row-actions">
              <button type="button" className="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="primary" disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Sheet>
      )}
    </section>
  );
}
